import { ReactElement, useMemo, useCallback } from 'react';
import { Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { getStringifyUrlQueryString } from '~utils/router';
import { isPlanCorporate } from '~utils/plans';
import { openUrlInNewTab } from '~utils/commons';
import { PATHS } from '~constants/paths';
import { SCHEDULE_DEMO_URL } from '~constants/commons';
import { useSubscription } from '~hooks/useSubscription';

import { WhatsappTypeSelectorOptionProps } from './types';
import WhatsappTypeSelectorOptionStatusChip from './SelectorOptionStatusChip';
import {
  WhatsappTypeSelectorOptionContainer,
  WhatsappTypeSelectorOptionHeader,
  WhatsappTypeSelectorOptionHeaderTitle,
  WhatsappTypeSelectorOptionHeaderSubTitle,
  WhatsappTypeSelectorOptionActions,
  WhatsappTypeSelectorOptionActionsButton,
  WhatsappTypeSelectorOptionHelperText,
} from './styles';

export const WhatsappTypeSelectorOption = ({
  status,
  title,
  subTitle,
  caption,
  actionDisconnected,
}: WhatsappTypeSelectorOptionProps): ReactElement => {
  const navigate = useNavigate();
  const subscription = useSubscription();

  const isNotAvailable = useMemo(() => status === 'not-available', [status]);
  const isConnected = useMemo(() => status === 'connected', [status]);
  const isDisconnected = useMemo(() => status === 'disconnected', [status]);

  const isCurrentPlanCorporate = useMemo(
    () => isPlanCorporate(subscription),
    [subscription],
  );

  const handleRedirectPlansPackages = useCallback(() => {
    if (isCurrentPlanCorporate) {
      openUrlInNewTab(SCHEDULE_DEMO_URL);
    } else {
      navigate(
        getStringifyUrlQueryString({
          url: PATHS.CONFIG.SUBSCRIPTIONS.BASE,
          query: {
            currentTab: 'plans-packages',
          },
        }),
      );
    }
  }, [isCurrentPlanCorporate, navigate]);

  return (
    <WhatsappTypeSelectorOptionContainer selected={isConnected}>
      <WhatsappTypeSelectorOptionHeader>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <WhatsappTypeSelectorOptionHeaderTitle selected={isConnected}>
            {title}
          </WhatsappTypeSelectorOptionHeaderTitle>

          <WhatsappTypeSelectorOptionStatusChip type={status} />
        </Stack>

        <WhatsappTypeSelectorOptionHeaderSubTitle>
          {subTitle}
        </WhatsappTypeSelectorOptionHeaderSubTitle>

        <WhatsappTypeSelectorOptionHelperText>
          {caption}
        </WhatsappTypeSelectorOptionHelperText>
      </WhatsappTypeSelectorOptionHeader>

      <WhatsappTypeSelectorOptionActions>
        {isNotAvailable && (
          <WhatsappTypeSelectorOptionActionsButton
            variant="outlined"
            size="small"
            onClick={handleRedirectPlansPackages}
          >
            {isCurrentPlanCorporate
              ? 'Contactar al equipo de ventas'
              : 'Adquirir'}
          </WhatsappTypeSelectorOptionActionsButton>
        )}

        {isDisconnected && actionDisconnected && (
          <WhatsappTypeSelectorOptionActionsButton
            variant="outlined"
            size="small"
            onClick={actionDisconnected.onClick}
          >
            {actionDisconnected.label}
          </WhatsappTypeSelectorOptionActionsButton>
        )}
      </WhatsappTypeSelectorOptionActions>
    </WhatsappTypeSelectorOptionContainer>
  );
};

export default WhatsappTypeSelectorOption;
